// Reine Rechnung zu den Meldungen über das Protokoll (docs/protokoll.md,
// „Meldungen über das Protokoll"): Verteiler an mehrere Abnehmer und das
// Bündeln von Neuladen-Aufträgen. Ohne Svelte und ohne Browser, damit
// testbar (lib/protokoll-meldungen.test.mjs); meldungen.svelte.ts hält nur
// das gemeinsame Abo.

/** Eine Meldung des Topics `meldung` (Wörter statt Nummern, wie der Client sie
 *  liefert). Alle Felder können fehlen. */
export interface MeldungsRahmen {
  art?: "neu" | "quittiert" | "aktualisiert" | null;
  domain?: string | null;
  type?: string | null;
  severity?: string | null;
  entity?: string | null;
  summary?: string | null;
  zeit?: number | null;
}

/** Pseudo-Domain der Quittierungs-Änderungen aus /v2-api/alert-acks
 *  (internal/audit: DomaeneAlertAcks) -- keine Zeile in der events-Tabelle. */
export const DOMAENE_ALERT_ACKS = "alert_acks";

/** Betrifft die Meldung die Quittierungen des Banners (alert_acks)? */
export function istAckAenderung(m: MeldungsRahmen | null): boolean {
  return m?.domain === DOMAENE_ALERT_ACKS;
}

/** Ändert die Meldung (oder die Lücke) eine Zeile der events-Tabelle? Nur
 *  die Quittierungs-Änderungen aus alert_acks tun das nicht. */
export function betrifftEvents(m: MeldungsRahmen | null): boolean {
  return !istAckAenderung(m);
}

/** Filter für eine Liste, die nur einen Teil der Meldungen zeigt (Banner ohne
 *  Routine, kritische Lage nur `critical`). Eine NEUE oder AKTUALISIERTE
 *  Meldung eines ausgeschlossenen Typs bzw. einer anderen Schwere lässt die
 *  Liste unverändert -- in einer Backup-Welle (40 `backup_ok` je Stunde) lud
 *  sonst jede Liste bei jeder Meldung neu. Immer neu geladen wird bei Lücke,
 *  Quittierung (eine Entwarnung quittiert unter fremdem Typ und ohne die
 *  Schwere der geschlossenen Zeile) und wenn Typ bzw. Schwere fehlen. */
export function betrifftListe(opt: {
  ohneTypen?: readonly string[];
  schweren?: readonly string[];
}): (m: MeldungsRahmen | null) => boolean {
  const ohne = new Set(opt.ohneTypen ?? []);
  const nur = opt.schweren ? new Set(opt.schweren) : undefined;
  return (m) => {
    if (!betrifftEvents(m)) return false;
    if (m === null || (m.art !== "neu" && m.art !== "aktualisiert")) return true;
    if (m.type && ohne.has(m.type)) return false;
    if (nur && m.severity && !nur.has(m.severity)) return false;
    return true;
  };
}

export type Abnehmer = (m: MeldungsRahmen | null) => void;

/** Verteilt jede Meldung an alle Abnehmer; `null` heißt Lücke (alles neu laden).
 *  Ein Abnehmer, der wirft, stört die anderen nicht. */
export class Verteiler {
  private readonly _abnehmer = new Set<Abnehmer>();

  /** Anzahl der Abnehmer. */
  get groesse(): number {
    return this._abnehmer.size;
  }

  /** Abnehmer eintragen; Rückgabe = austragen. Derselbe Abnehmer zweimal zählt
   *  zweimal. */
  hinzu(fn: Abnehmer): () => void {
    const eintrag: Abnehmer = (m) => fn(m);
    this._abnehmer.add(eintrag);
    return () => {
      this._abnehmer.delete(eintrag);
    };
  }

  verteile(m: MeldungsRahmen | null): void {
    for (const fn of [...this._abnehmer]) {
      try {
        fn(m);
      } catch (e) {
        console.error("meldung: Abnehmer hat geworfen", e);
      }
    }
  }
}

export interface Uhr {
  setTimeout: (f: () => void, ms: number) => unknown;
  clearTimeout: (t: unknown) => void;
}

const ECHTE_UHR: Uhr = {
  setTimeout: (f, ms) => setTimeout(f, ms),
  clearTimeout: (t) => clearTimeout(t as ReturnType<typeof setTimeout>),
};

/** Bündelt Aufträge: `ausloesen()` startet beim ersten Aufruf ein Fenster von
 *  `ms`; Aufrufe darin zählen nicht mehr, am Ende läuft `fn` genau einmal
 *  (Fenster ab dem ERSTEN Auftrag, nicht verschoben -- ein Dauerfeuer von
 *  Meldungen darf das Neuladen nicht aufschieben). `abbrechen()` verwirft einen
 *  offenen Auftrag. */
export function buendel(fn: () => void, ms: number, uhr: Uhr = ECHTE_UHR) {
  let timer: unknown;
  let offen = false;
  return {
    ausloesen(): void {
      if (offen) return;
      offen = true;
      timer = uhr.setTimeout(() => {
        offen = false;
        timer = undefined;
        fn();
      }, ms);
    },
    abbrechen(): void {
      if (!offen) return;
      uhr.clearTimeout(timer);
      offen = false;
      timer = undefined;
    },
  };
}

export interface NachladeOptionen {
  /** Bündel-Fenster in ms. */
  ms: number;
  /** Welche Meldungen zählen? Eine Lücke (`null`) zählt immer. Ohne Angabe
   *  zählt jede. */
  filter?: (m: MeldungsRahmen) => boolean;
}

/** Die Entscheidung hinter `meldungRefetch`: Meldung (oder Lücke) kommt, der
 *  Filter lässt sie durch, `refresh` läuft einmal je Fenster. `bei` ist der
 *  Abnehmer, `abbrechen` verwirft einen offenen Auftrag. */
export function meldungNachlader(
  refresh: () => void | Promise<void>,
  opt: NachladeOptionen,
  uhr: Uhr = ECHTE_UHR,
) {
  const b = buendel(() => void refresh(), opt.ms, uhr);
  return {
    bei(m: MeldungsRahmen | null): void {
      if (m === null || !opt.filter || opt.filter(m)) b.ausloesen();
    },
    abbrechen: () => b.abbrechen(),
  };
}
